import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapPin, Plus, Trash2, Users, Search, Loader2, Pencil, Undo2, Crosshair, Ruler, Triangle, MapPinned, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { MapContainer, TileLayer, Polygon, Popup, useMap, useMapEvents, Circle as LeafletCircle, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_URL = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';
const DEFAULT_CENTER = [19.076, 72.8777];
const POINT_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/* ── Geo math ── */
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000, toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad, dLng = (lng2 - lng1) * toRad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function polyPerimeter(pts) {
  if (pts.length < 2) return 0;
  let p = 0;
  for (let i = 0; i < pts.length; i++) { const j = (i + 1) % pts.length; p += haversine(pts[i][0], pts[i][1], pts[j][0], pts[j][1]); }
  return p;
}
function polyArea(pts) {
  if (pts.length < 3) return 0;
  const cLat = pts.reduce((s, c) => s + c[0], 0) / pts.length;
  const cLng = pts.reduce((s, c) => s + c[1], 0) / pts.length;
  const mLat = 111320, mLng = 111320 * Math.cos(cLat * Math.PI / 180);
  const pr = pts.map(c => [(c[0] - cLat) * mLat, (c[1] - cLng) * mLng]);
  let a = 0;
  for (let i = 0; i < pr.length; i++) { const j = (i + 1) % pr.length; a += pr[i][0] * pr[j][1] - pr[j][0] * pr[i][1]; }
  return Math.abs(a) / 2;
}
function fmtArea(sqm) {
  if (sqm >= 1e6) return `${(sqm / 1e6).toFixed(2)} sq km`;
  if (sqm >= 1e4) return `${(sqm / 1e4).toFixed(1)} ha`;
  return `${Math.round(sqm)} sq m`;
}
function fmtDist(m) { return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`; }

function zoneTypeLabel(z) {
  if (z.center_lat && z.center_lng && z.radius_meters) return 'Circle';
  if (z.coordinates?.length >= 3) return `${z.coordinates.length}-point`;
  return 'Undefined';
}

/* ── Draggable numbered marker ── */
const ptIcon = (i) => L.divIcon({
  className: '',
  html: `<div style="background:#026536;color:#fff;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font:700 11px/1 system-ui;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);cursor:grab">${i + 1}</div>`,
  iconSize: [24, 24], iconAnchor: [12, 12],
});

/* ── Map sub-components ── */
function MapClick({ onClick }) { useMapEvents({ click(e) { onClick(e.latlng); } }); return null; }
function FlyTo({ center, zoom, flyKey }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, zoom || 13, { duration: 1 }); }, [center, zoom, flyKey, map]);
  return null;
}
function DragPoint({ position, index, onDrag }) {
  const ref = useRef(null);
  const h = useMemo(() => ({
    dragend() { const m = ref.current; if (m) { const { lat, lng } = m.getLatLng(); onDrag(index, [lat, lng]); } },
  }), [index, onDrag]);
  return <Marker draggable position={position} ref={ref} eventHandlers={h} icon={ptIcon(index)} />;
}

/* ══════════════════════════════════════════════
   Zone Detail Dialog — matches reference design
   ══════════════════════════════════════════════ */
function ZoneDetailDialog({ zone, open, onClose, employees, onEdit, onDelete, onAssign }) {
  const [tab, setTab] = useState('overview');
  if (!zone) return null;

  const isCircle = !!(zone.center_lat && zone.center_lng && zone.radius_meters);
  const isPoly = zone.coordinates?.length >= 3;
  const centerCoords = isCircle
    ? `${zone.center_lat.toFixed(6)},  ${zone.center_lng.toFixed(6)}`
    : isPoly
      ? `${(zone.coordinates.reduce((s, c) => s + c[0], 0) / zone.coordinates.length).toFixed(6)},  ${(zone.coordinates.reduce((s, c) => s + c[1], 0) / zone.coordinates.length).toFixed(6)}`
      : '-';
  const radiusStr = isCircle ? `${(zone.radius_meters / 1000).toFixed(1)} km` : isPoly ? fmtDist(polyPerimeter(zone.coordinates) / Math.PI) : '-';
  const typeStr = isCircle ? 'Circle' : isPoly ? `${zone.coordinates.length}-point` : '-';
  const areaStr = isCircle ? fmtArea(Math.PI * zone.radius_meters * zone.radius_meters) : isPoly ? fmtArea(polyArea(zone.coordinates)) : '-';
  const perimeterStr = isCircle ? fmtDist(2 * Math.PI * zone.radius_meters) : isPoly ? fmtDist(polyPerimeter(zone.coordinates)) : '-';

  const assignedEmps = employees.filter(e => (e.zone_ids || []).includes(zone.id));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0 gap-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border/30">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${zone.color || '#026536'}15`, color: zone.color || '#026536' }}>
                <MapPinned size={22} />
              </div>
              <div>
                <h2 data-testid="detail-zone-name" className="text-xl font-bold text-[#1a2529]">{zone.name}</h2>
                <p className="text-sm text-[#1a2529]/50 mt-0.5">
                  {zone.hub_code && <span className="font-semibold text-[#1a2529]/70">{zone.hub_code}</span>}
                  {zone.hub_code && zone.region && <span> &middot; </span>}
                  {zone.region && <span>{zone.region}</span>}
                  {!zone.hub_code && !zone.region && <span>{zone.employee_count || 0} employees assigned</span>}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              <button data-testid="detail-edit-btn" onClick={() => { onClose(); onEdit(zone); }}
                className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-[#026536]/10 text-[#026536] transition-colors">
                <Pencil size={15} />
              </button>
              <button data-testid="detail-delete-btn" onClick={() => { onClose(); onDelete(zone.id); }}
                className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-red-50 text-red-400 transition-colors">
                <Trash2 size={15} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex mt-4 bg-[#f4f4f5] rounded-lg p-1">
            {['overview', 'staff'].map(t => (
              <button key={t} data-testid={`detail-tab-${t}`} onClick={() => setTab(t)}
                className={`flex-1 h-9 rounded-md text-sm font-semibold transition-all ${tab === t ? 'bg-white text-[#1a2529] shadow-sm' : 'text-[#1a2529]/50 hover:text-[#1a2529]/70'}`}>
                {t === 'overview' ? 'Overview' : 'Staff'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-6 py-5">
          {tab === 'overview' && (
            <div className="space-y-4">
              {/* Address */}
              {zone.address && (
                <div className="flex items-center gap-2 bg-[#f4f4f5]/80 rounded-lg px-4 py-3">
                  <MapPin size={14} className="text-[#1a2529]/40 shrink-0" />
                  <span className="text-sm text-[#1a2529]/70">{zone.address}</span>
                </div>
              )}

              {/* Triangulation Perimeter Card */}
              <div data-testid="triangulation-card" className="rounded-xl border-2 border-blue-200 bg-blue-50/50 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Triangle size={18} className="text-blue-600" />
                  <h3 className="text-base font-bold text-[#1a2529]">Triangulation Perimeter</h3>
                </div>

                {/* Center / Radius / Type row */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-xs font-semibold text-blue-600 mb-1">Center</p>
                    <p data-testid="detail-center" className="text-sm font-mono text-[#1a2529]/80">{centerCoords}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 mb-1">Radius</p>
                    <p data-testid="detail-radius" className="text-sm font-mono text-[#1a2529]/80">{radiusStr}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 mb-1">Type</p>
                    <p data-testid="detail-type" className="text-sm font-mono text-[#1a2529]/80">{typeStr}</p>
                  </div>
                </div>

                {/* Named Perimeter Points */}
                {isCircle && (
                  <div className="grid grid-cols-1 gap-2">
                    <div className="bg-white rounded-lg border border-blue-100 px-4 py-3 text-center">
                      <p className="text-sm font-bold text-blue-600 mb-0.5">Center</p>
                      <p className="text-xs font-mono text-[#1a2529]/60">{zone.center_lat.toFixed(4)},  {zone.center_lng.toFixed(4)}</p>
                    </div>
                  </div>
                )}
                {isPoly && (
                  <div className={`grid gap-2 ${zone.coordinates.length <= 4 ? `grid-cols-${Math.min(zone.coordinates.length, 4)}` : 'grid-cols-3'}`}
                    style={{ gridTemplateColumns: `repeat(${Math.min(zone.coordinates.length, 4)}, 1fr)` }}>
                    {zone.coordinates.map((pt, i) => (
                      <div key={`coord-${pt[0]}-${pt[1]}`} data-testid={`detail-point-${i}`} className="bg-white rounded-lg border border-blue-100 px-3 py-3 text-center">
                        <p className="text-sm font-bold text-blue-600 mb-0.5">{POINT_LABELS[i] || (i + 1)}</p>
                        <p className="text-xs font-mono text-[#1a2529]/60">{pt[0].toFixed(4)},  {pt[1].toFixed(4)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Area & Perimeter summary */}
              <div className="flex gap-3">
                <div className="flex-1 bg-[#f4f4f5]/80 rounded-lg px-4 py-3 text-center">
                  <p className="text-[10px] font-semibold text-[#1a2529]/40 uppercase tracking-wider mb-0.5">Area</p>
                  <p className="text-sm font-bold text-[#1a2529]">{areaStr}</p>
                </div>
                <div className="flex-1 bg-[#f4f4f5]/80 rounded-lg px-4 py-3 text-center">
                  <p className="text-[10px] font-semibold text-[#1a2529]/40 uppercase tracking-wider mb-0.5">Perimeter</p>
                  <p className="text-sm font-bold text-[#1a2529]">{perimeterStr}</p>
                </div>
              </div>
            </div>
          )}

          {tab === 'staff' && (
            <div className="space-y-2">
              {assignedEmps.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="mx-auto text-[#1a2529]/15 mb-2" size={32} />
                  <p className="text-sm text-[#1a2529]/40">No employees assigned</p>
                  <Button onClick={() => { onClose(); onAssign(zone); }} size="sm" variant="outline"
                    className="mt-3 text-xs font-medium border-[#026536]/20 text-[#026536]">
                    <Plus size={13} className="mr-1" /> Assign Employees
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-[#1a2529]/40 mb-2">{assignedEmps.length} employee{assignedEmps.length !== 1 ? 's' : ''} assigned</p>
                  {assignedEmps.map((emp, i) => (
                    <div key={emp.id} data-testid={`detail-staff-${i}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#f4f4f5]/50 hover:bg-[#f4f4f5]">
                      <div className="h-8 w-8 rounded-full bg-[#026536]/10 flex items-center justify-center text-[#026536] text-xs font-bold">
                        {(emp.name || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1a2529] truncate">{emp.name}</p>
                        <p className="text-xs text-[#1a2529]/40">+91 {emp.phone}</p>
                      </div>
                      {emp.designation && <span className="text-[10px] text-[#1a2529]/30">{emp.designation}</span>}
                    </div>
                  ))}
                  <Button onClick={() => { onClose(); onAssign(zone); }} size="sm" variant="outline"
                    className="w-full mt-2 text-xs font-medium border-[#026536]/20 text-[#026536]">
                    <Pencil size={12} className="mr-1" /> Edit Assignments
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ══════════════════════════════════
   Main AdminZones Component
   ══════════════════════════════════ */
export default function AdminZones() {
  const [zones, setZones] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [detailZone, setDetailZone] = useState(null);
  const [selZone, setSelZone] = useState(null);
  const [zoneName, setZoneName] = useState('');
  const [hubCode, setHubCode] = useState('');
  const [zoneRegion, setZoneRegion] = useState('');
  const [zoneAddress, setZoneAddress] = useState('');
  const [zoneColor, setZoneColor] = useState('#026536');
  const [saving, setSaving] = useState(false);
  const [selEmps, setSelEmps] = useState([]);
  const [editing, setEditing] = useState(null);

  const [mode, setMode] = useState('search');
  const [searchQ, setSearchQ] = useState('');
  const [searchRes, setSearchRes] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selLoc, setSelLoc] = useState(null);
  const [radius, setRadius] = useState(10000);
  const [pts, setPts] = useState([]);
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyKey, setFlyKey] = useState(0);
  const [mainFly, setMainFly] = useState(null);
  const [mainFlyKey, setMainFlyKey] = useState(0);
  const searchTimer = useRef(null);
  const [coordInput, setCoordInput] = useState('');
  const [radiusInput, setRadiusInput] = useState('10');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [zR, eR, bR] = await Promise.all([adminAPI.getZones(), adminAPI.getEmployees('approved'), adminAPI.getBranches()]);
      setZones(zR.data);
      setEmployees(eR.data.employees || eR.data);
      setBranches(bR.data || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleSearch = (q) => {
    setSearchQ(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.trim().length < 2) { setSearchRes([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=8&countrycodes=in&addressdetails=1`);
        const data = await res.json();
        setSearchRes(data.map(r => ({ name: r.display_name, lat: +r.lat, lng: +r.lon })));
      } catch { toast.error('Search failed'); }
      finally { setSearching(false); }
    }, 400);
  };

  const pickResult = (r) => {
    setSelLoc(r);
    setFlyTarget({ lat: r.lat, lng: r.lng });
    setFlyKey(k => k + 1);
    setSearchRes([]);
    setSearchQ(r.name.split(',')[0]);
    if (!zoneName) setZoneName(r.name.split(',')[0]);
  };

  const onMapClick = (ll) => {
    if (mode === 'search') {
      setSelLoc({ lat: ll.lat, lng: ll.lng, name: `${ll.lat.toFixed(6)}, ${ll.lng.toFixed(6)}` });
    } else {
      setPts(p => [...p, [ll.lat, ll.lng]]);
    }
  };

  const onDragPoint = useCallback((idx, newPos) => {
    setPts(p => { const c = [...p]; c[idx] = newPos; return c; });
  }, []);

  const addCoord = () => {
    const raw = coordInput.trim();
    if (!raw) return;
    const parts = raw.split(/[,\s]+/).map(Number);
    if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1]) && Math.abs(parts[0]) <= 90 && Math.abs(parts[1]) <= 180) {
      setPts(p => [...p, [parts[0], parts[1]]]);
      setCoordInput('');
      setFlyTarget({ lat: parts[0], lng: parts[1] });
      setFlyKey(k => k + 1);
    } else {
      toast.error('Invalid format. Use: lat, lng (e.g. 19.076, 72.877)');
    }
  };

  const removePoint = (idx) => setPts(p => p.filter((_, i) => i !== idx));

  const handleRadiusKm = (val) => {
    setRadiusInput(val);
    const km = parseFloat(val);
    if (!isNaN(km) && km > 0 && km <= 25) setRadius(Math.round(km * 1000));
  };
  const handleRadiusSlider = (val) => { setRadius(val); setRadiusInput((val / 1000).toFixed(1)); };

  const openEdit = (zone) => {
    setEditing(zone);
    setZoneName(zone.name);
    setHubCode(zone.hub_code || '');
    setZoneRegion(zone.region || '');
    setZoneAddress(zone.address || '');
    setZoneColor(zone.color || '#026536');
    if (zone.center_lat && zone.center_lng && zone.radius_meters) {
      setMode('search');
      setSelLoc({ lat: zone.center_lat, lng: zone.center_lng, name: `${zone.center_lat.toFixed(6)}, ${zone.center_lng.toFixed(6)}` });
      setRadius(zone.radius_meters);
      setRadiusInput((zone.radius_meters / 1000).toFixed(1));
      setFlyTarget({ lat: zone.center_lat, lng: zone.center_lng });
      setFlyKey(k => k + 1);
    } else if (zone.coordinates?.length >= 3) {
      setMode('draw');
      setPts([...zone.coordinates]);
      const cLat = zone.coordinates.reduce((s, c) => s + c[0], 0) / zone.coordinates.length;
      const cLng = zone.coordinates.reduce((s, c) => s + c[1], 0) / zone.coordinates.length;
      setFlyTarget({ lat: cLat, lng: cLng });
      setFlyKey(k => k + 1);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!zoneName.trim()) { toast.error('Enter zone name'); return; }
    if (mode === 'search' && !selLoc) { toast.error('Select a location on the map or via search'); return; }
    if (mode === 'draw' && pts.length < 3) { toast.error('Need at least 3 boundary points'); return; }
    setSaving(true);
    try {
      const payload = { name: zoneName, color: zoneColor, hub_code: hubCode, region: zoneRegion, address: zoneAddress };
      if (mode === 'search') {
        payload.center_lat = selLoc.lat;
        payload.center_lng = selLoc.lng;
        payload.radius_meters = radius;
        payload.coordinates = [];
      } else {
        payload.coordinates = pts;
      }
      if (editing) {
        await adminAPI.updateZone(editing.id, payload);
        toast.success('Zone updated');
      } else {
        await adminAPI.createZone(payload);
        toast.success('Zone created');
      }
      resetDialog();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save zone');
    } finally { setSaving(false); }
  };

  const resetDialog = () => {
    setDialogOpen(false); setEditing(null); setZoneName(''); setHubCode(''); setZoneRegion(''); setZoneAddress('');
    setZoneColor('#026536'); setSelLoc(null); setSearchQ(''); setSearchRes([]);
    setRadius(10000); setRadiusInput('10'); setPts([]); setMode('search'); setFlyTarget(null); setCoordInput('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this zone? Employees will be unassigned.')) return;
    try { await adminAPI.deleteZone(id); toast.success('Zone deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const openAssign = (z) => {
    setSelZone(z);
    setSelEmps(employees.filter(e => (e.zone_ids || []).includes(z.id)).map(e => e.id));
    setAssignOpen(true);
  };

  const handleAssign = async () => {
    if (!selZone) return;
    setSaving(true);
    try { await adminAPI.assignEmployees(selZone.id, selEmps); toast.success('Employees assigned'); setAssignOpen(false); fetchData(); }
    catch { toast.error('Failed to assign'); }
    finally { setSaving(false); }
  };

  const toggleEmp = (id) => setSelEmps(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const drawMetrics = useMemo(() => {
    if (mode === 'search' && selLoc && radius) return { area: fmtArea(Math.PI * radius * radius), perimeter: fmtDist(2 * Math.PI * radius) };
    if (mode === 'draw' && pts.length >= 3) return { area: fmtArea(polyArea(pts)), perimeter: fmtDist(polyPerimeter(pts)) };
    return null;
  }, [mode, selLoc, radius, pts]);

  const dialogZoom = useMemo(() => {
    if (mode === 'search') return radius > 15000 ? 10 : radius > 8000 ? 11 : radius > 3000 ? 12 : 14;
    return 13;
  }, [mode, radius]);

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 data-testid="zones-title" className="text-2xl font-extrabold text-[#1a2529] tracking-tight">Hub Locations</h1>
            <p className="text-sm text-[#1a2529]/50 mt-0.5">Define hub operational boundaries with triangulation perimeters</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetDialog(); else { setEditing(null); setDialogOpen(true); } }}>
            <DialogTrigger asChild>
              <Button data-testid="create-zone-btn" className="h-10 bg-[#026536] hover:bg-[#026536]/90 gap-2 font-medium">
                <Plus size={15} /> New Hub Zone
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-bold text-[#1a2529]">{editing ? 'Edit Hub Zone' : 'Create Hub Zone'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Hub Identity */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <Label className="text-xs font-semibold">Hub Code</Label>
                    <Input data-testid="hub-code-input" value={hubCode} onChange={e => setHubCode(e.target.value)}
                      placeholder="HH001" className="h-10 mt-1 font-mono" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs font-semibold">Hub Name</Label>
                    <Input data-testid="zone-name-input" value={zoneName} onChange={e => setZoneName(e.target.value)}
                      placeholder="e.g., Kurla West" className="h-10 mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Region / Branch</Label>
                    <Input data-testid="zone-region-input" value={zoneRegion} onChange={e => setZoneRegion(e.target.value)}
                      placeholder="e.g., Mumbai South" className="h-10 mt-1" list="branch-list" />
                    <datalist id="branch-list">
                      {branches.map(b => <option key={b.id} value={b.name} />)}
                    </datalist>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Address</Label>
                    <Input data-testid="zone-address-input" value={zoneAddress} onChange={e => setZoneAddress(e.target.value)}
                      placeholder="Hub address" className="h-10 mt-1" />
                  </div>
                </div>

                {/* Mode Tabs */}
                <div className="flex gap-2">
                  <button data-testid="mode-search" onClick={() => setMode('search')}
                    className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-semibold transition-all ${mode === 'search' ? 'bg-[#026536] text-white' : 'bg-[#f4f4f5] text-[#1a2529]/60 hover:bg-[#e4e4e5]'}`}>
                    <Crosshair size={14} /> Center + Radius
                  </button>
                  <button data-testid="mode-draw" onClick={() => setMode('draw')}
                    className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-semibold transition-all ${mode === 'draw' ? 'bg-[#026536] text-white' : 'bg-[#f4f4f5] text-[#1a2529]/60 hover:bg-[#e4e4e5]'}`}>
                    <Triangle size={14} /> Polygon Boundary
                  </button>
                </div>

                {/* Color */}
                <div className="flex items-center gap-3">
                  <Label className="text-xs font-semibold shrink-0">Color</Label>
                  <input type="color" value={zoneColor} onChange={e => setZoneColor(e.target.value)} className="h-8 w-8 rounded cursor-pointer border-0" />
                  <Input value={zoneColor} onChange={e => setZoneColor(e.target.value)} className="h-8 w-24 text-xs" />
                </div>

                {/* Circle Mode */}
                {mode === 'search' && (
                  <div className="space-y-3">
                    <div className="relative">
                      <Label className="text-xs font-semibold">Search Location</Label>
                      <div className="relative mt-1">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1a2529]/40" />
                        <Input data-testid="location-search-input" value={searchQ} onChange={e => handleSearch(e.target.value)}
                          placeholder="Station, metro, hub, city..." className="h-10 pl-9" />
                        {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#026536]" />}
                      </div>
                      {searchRes.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-border/40 max-h-48 overflow-y-auto">
                          {searchRes.map((r, i) => (
                            <button key={`search-${r.lat}-${r.lon}`} data-testid={`search-result-${i}`} onClick={() => pickResult(r)}
                              className="w-full text-left px-3 py-2.5 hover:bg-[#026536]/5 transition-colors border-b border-border/20 last:border-0">
                              <p className="text-xs font-medium text-[#1a2529] line-clamp-1">{r.name.split(',')[0]}</p>
                              <p className="text-[10px] text-[#1a2529]/40 line-clamp-1 mt-0.5">{r.name}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {selLoc && (
                      <div className="bg-[#026536]/5 rounded-lg p-3 flex items-center gap-2">
                        <MapPin size={14} className="text-[#026536] shrink-0" />
                        <p className="text-xs text-[#1a2529] font-medium truncate">{selLoc.name}</p>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs font-semibold">Radius</Label>
                        <div className="flex items-center gap-1.5">
                          <Input data-testid="radius-km-input" value={radiusInput} onChange={e => handleRadiusKm(e.target.value)}
                            className="h-7 w-16 text-xs text-center font-semibold" />
                          <span className="text-xs text-[#1a2529]/50 font-medium">km</span>
                        </div>
                      </div>
                      <input data-testid="radius-slider" type="range" min="100" max="25000" step="100" value={radius}
                        onChange={e => handleRadiusSlider(parseInt(e.target.value))} className="w-full accent-[#026536]" />
                      <div className="flex justify-between text-[10px] text-[#1a2529]/40 mt-0.5"><span>100 m</span><span>25 km</span></div>
                    </div>
                  </div>
                )}

                {/* Polygon Mode */}
                {mode === 'draw' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold">Boundary Points ({pts.length})</Label>
                      <div className="flex gap-2">
                        {pts.length > 0 && (
                          <button data-testid="undo-point-btn" onClick={() => setPts(p => p.slice(0, -1))}
                            className="text-xs text-[#026536] font-medium hover:underline flex items-center gap-1"><Undo2 size={12} /> Undo</button>
                        )}
                        {pts.length > 0 && (
                          <button data-testid="clear-points-btn" onClick={() => setPts([])}
                            className="text-xs text-red-500 font-medium hover:underline">Clear All</button>
                        )}
                      </div>
                    </div>
                    <p className="text-[11px] text-[#1a2529]/40">Click map or enter coordinates. Drag numbered markers to adjust.</p>
                    <div className="flex gap-2">
                      <Input data-testid="coord-input" value={coordInput} onChange={e => setCoordInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addCoord()}
                        placeholder="lat, lng (e.g. 19.076, 72.877)" className="h-9 text-xs flex-1" />
                      <Button data-testid="add-coord-btn" onClick={addCoord} size="sm" variant="outline"
                        className="h-9 text-xs font-medium border-[#026536]/30 text-[#026536] shrink-0">
                        <Plus size={13} className="mr-1" /> Add
                      </Button>
                    </div>
                    {pts.length > 0 && (
                      <div className="max-h-36 overflow-y-auto rounded-lg border border-border/30 divide-y divide-border/20">
                        {pts.map((p, i) => (
                          <div key={`pt-${p[0]}-${p[1]}`} className="flex items-center justify-between px-3 py-1.5 text-xs hover:bg-[#f4f4f5]/50">
                            <span className="font-mono text-[#1a2529]/70">
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold mr-2">{POINT_LABELS[i] || (i+1)}</span>
                              {p[0].toFixed(6)}, {p[1].toFixed(6)}
                            </span>
                            <button data-testid={`remove-pt-${i}`} onClick={() => removePoint(i)}
                              className="text-red-400 hover:text-red-600 p-1"><Trash2 size={12} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Map */}
                <div className="w-full h-[280px] rounded-xl overflow-hidden border border-border/50">
                  <MapContainer center={DEFAULT_CENTER} zoom={11} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                    <TileLayer attribution={TILE_ATTR} url={TILE_URL} />
                    <MapClick onClick={onMapClick} />
                    {flyTarget && <FlyTo center={[flyTarget.lat, flyTarget.lng]} zoom={dialogZoom} flyKey={flyKey} />}
                    {mode === 'search' && selLoc && (
                      <>
                        <Marker position={[selLoc.lat, selLoc.lng]} />
                        <LeafletCircle center={[selLoc.lat, selLoc.lng]} radius={radius}
                          pathOptions={{ color: zoneColor, fillOpacity: 0.12, weight: 2, dashArray: '6 4' }} />
                      </>
                    )}
                    {mode === 'draw' && (
                      <>
                        {pts.length >= 3 && <Polygon positions={pts} pathOptions={{ color: zoneColor, fillOpacity: 0.12, weight: 2 }} />}
                        {pts.map((p, i) => <DragPoint key={`${i}-${p[0].toFixed(6)}-${p[1].toFixed(6)}`} position={p} index={i} onDrag={onDragPoint} />)}
                      </>
                    )}
                  </MapContainer>
                </div>

                {/* Live metrics */}
                {drawMetrics && (
                  <div className="flex items-center gap-3 bg-blue-50/50 border border-blue-200 rounded-lg px-4 py-2.5">
                    <Ruler size={14} className="text-blue-600 shrink-0" />
                    <div className="flex gap-4 text-xs">
                      <span className="text-[#1a2529]/70">Area: <strong className="text-[#1a2529]">{drawMetrics.area}</strong></span>
                      <span className="text-[#1a2529]/70">Perimeter: <strong className="text-[#1a2529]">{drawMetrics.perimeter}</strong></span>
                    </div>
                  </div>
                )}

                <Button data-testid="save-zone-btn" onClick={handleSave} disabled={saving}
                  className="w-full h-11 bg-[#026536] hover:bg-[#026536]/90 font-semibold">
                  {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                  {editing ? 'Update Hub Zone' : 'Create Hub Zone'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Main Map */}
        <div className="w-full h-[300px] md:h-[400px] rounded-xl overflow-hidden border border-border/40 shadow-sm">
          <MapContainer center={DEFAULT_CENTER} zoom={10} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <TileLayer attribution={TILE_ATTR} url={TILE_URL} />
            {mainFly && <FlyTo center={mainFly.center} zoom={mainFly.zoom} flyKey={mainFlyKey} />}
            {zones.map(z => {
              if (z.center_lat && z.center_lng && z.radius_meters) {
                return (
                  <LeafletCircle key={z.id} center={[z.center_lat, z.center_lng]} radius={z.radius_meters}
                    pathOptions={{ color: z.color || '#026536', fillOpacity: 0.12, weight: 2 }}>
                    <Popup><div className="font-sans"><p className="font-bold text-sm">{z.name}</p>
                      {z.hub_code && <p className="text-xs text-gray-500">{z.hub_code}{z.region ? ` · ${z.region}` : ''}</p>}
                      <p className="text-xs text-gray-400">{z.employee_count || 0} staff | {fmtDist(z.radius_meters)} radius</p>
                    </div></Popup>
                  </LeafletCircle>
                );
              }
              if (z.coordinates?.length >= 3) {
                return (
                  <Polygon key={z.id} positions={z.coordinates} pathOptions={{ color: z.color || '#026536', fillOpacity: 0.12, weight: 2 }}>
                    <Popup><div className="font-sans"><p className="font-bold text-sm">{z.name}</p>
                      {z.hub_code && <p className="text-xs text-gray-500">{z.hub_code}{z.region ? ` · ${z.region}` : ''}</p>}
                      <p className="text-xs text-gray-400">{z.employee_count || 0} staff | {z.coordinates.length}-pt boundary</p>
                    </div></Popup>
                  </Polygon>
                );
              }
              return null;
            })}
          </MapContainer>
        </div>

        {/* Zone Cards */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" /></div>
        ) : zones.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm">
            <MapPinned className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
            <p className="font-medium text-[#1a2529]/50">No hub zones created yet</p>
            <p className="text-xs text-[#1a2529]/30 mt-1">Create your first hub zone to define operational areas</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {zones.map((z, idx) => (
              <div key={z.id} data-testid={`zone-card-${idx}`}
                onClick={() => setDetailZone(z)}
                className="bg-white rounded-xl p-4 border border-border/30 shadow-sm hover:shadow-md hover:border-[#026536]/20 transition-all cursor-pointer group">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${z.color || '#026536'}15`, color: z.color || '#026536' }}>
                      <MapPinned size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1a2529]">{z.name}</p>
                      <p className="text-xs text-[#1a2529]/40">
                        {z.hub_code && <span className="font-semibold text-[#1a2529]/60">{z.hub_code}</span>}
                        {z.hub_code && z.region && <span> · </span>}
                        {z.region && <span>{z.region}</span>}
                        {!z.hub_code && !z.region && <span>{z.employee_count || 0} employees</span>}
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-[#1a2529]/20 group-hover:text-[#026536] transition-colors mt-1" />
                </div>
                {/* Mini triangulation info */}
                <div className="mt-3 bg-blue-50/50 rounded-lg border border-blue-100 px-3 py-2">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Triangle size={12} className="text-blue-500" />
                    <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">Perimeter</span>
                  </div>
                  <div className="flex gap-3 text-[11px]">
                    <span className="text-[#1a2529]/50">Type: <strong className="text-[#1a2529]/80">{zoneTypeLabel(z)}</strong></span>
                    <span className="text-[#1a2529]/50">
                      {z.radius_meters ? <>Radius: <strong className="text-[#1a2529]/80">{fmtDist(z.radius_meters)}</strong></> :
                        z.coordinates?.length >= 3 ? <>Points: <strong className="text-[#1a2529]/80">{z.coordinates.length}</strong></> : '-'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <Badge variant="outline" className="text-[10px] font-medium px-2 py-0.5 border-[#1a2529]/10 text-[#1a2529]/50">
                    <Users size={10} className="mr-1" /> {z.employee_count || 0} staff
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Zone Detail Dialog */}
        <ZoneDetailDialog
          zone={detailZone}
          open={!!detailZone}
          onClose={() => setDetailZone(null)}
          employees={employees}
          onEdit={openEdit}
          onDelete={handleDelete}
          onAssign={openAssign}
        />

        {/* Assign Dialog */}
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className="max-w-md max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-bold text-[#1a2529]">Assign to {selZone?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
              {employees.map((emp, idx) => (
                <label key={emp.id} data-testid={`assign-emp-${idx}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#f4f4f5] cursor-pointer transition-colors">
                  <input type="checkbox" checked={selEmps.includes(emp.id)} onChange={() => toggleEmp(emp.id)}
                    className="h-4 w-4 rounded border-[#1a2529]/20 text-[#026536] focus:ring-[#026536]" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#1a2529]">{emp.name}</p>
                    <p className="text-xs text-[#1a2529]/40">+91 {emp.phone}</p>
                  </div>
                  {emp.zone_ids?.length > 0 && !emp.zone_ids.includes(selZone?.id) && (
                    <Badge className="bg-[#f4f4f5] text-[#1a2529]/40 border-0 text-[10px]">Other Zone(s)</Badge>
                  )}
                </label>
              ))}
              {employees.length === 0 && <p className="text-center text-sm text-[#1a2529]/40 py-4">No approved employees</p>}
            </div>
            <Button data-testid="save-assign-btn" onClick={handleAssign} disabled={saving}
              className="w-full h-10 bg-[#026536] hover:bg-[#026536]/90 font-semibold">
              {saving && <Loader2 className="animate-spin mr-2" size={16} />}
              Save Assignments ({selEmps.length})
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
